import { AggressiveModeConfig } from '@cast/types';
import {
  RebalancingConfigurationNode,
  RebalancingConfigurationNodeComponent,
  RebalancingConfigurationNodeComponentType,
  RebalancingNodeIssuesWorkloadCategory,
} from '@cast/types-cluster-autoscaler';
import { convertUnit, toNumOrUndefined, toNumOrZero } from '@cast/utils';

import { notReachable } from 'utils/types/not-reachable';

const AGGRESSIVE_MODE_CONFIG_WITH_ALL_FLAGS_DISABLED: AggressiveModeConfig = {
  ignoreLocalPersistentVolumes: false,
  ignoreProblemJobPods: false,
  ignoreProblemRemovalDisabledPods: false,
  ignoreProblemPodsWithoutController: false,
  ignoreProblemPreventedDrainPods: false,
};

const workloadIssueToAggressiveConfigKey: Partial<
  Record<RebalancingNodeIssuesWorkloadCategory, keyof AggressiveModeConfig>
> = {
  LOCAL_PERSISTENT_VOLUME: 'ignoreLocalPersistentVolumes',
  REMOVAL_DISABLED: 'ignoreProblemRemovalDisabledPods',
  NO_CONTROLLER: 'ignoreProblemPodsWithoutController',
  JOB_POD: 'ignoreProblemJobPods',
  PREVENTED_DRAIN: 'ignoreProblemPreventedDrainPods',
};

type NodeAggressiveModeRequirement =
  | {
      type: 'not_needed';
    }
  | {
      type: 'required';
      config: AggressiveModeConfig;
    }
  | {
      type: 'incompatible';
    };

export const getNodeAggressiveModeRequirement = (
  node: RebalancingConfigurationNode
): NodeAggressiveModeRequirement => {
  if (node.status === 'READY') {
    return { type: 'not_needed' };
  }

  if (node.status !== 'AGGRESSIVE_ONLY') {
    return { type: 'incompatible' };
  }

  const config: AggressiveModeConfig = {
    ...AGGRESSIVE_MODE_CONFIG_WITH_ALL_FLAGS_DISABLED,
  };

  const workloadIssues = node.issues?.workload ?? [];
  for (const workloadIssue of workloadIssues) {
    const aggressiveConfigKey =
      workloadIssueToAggressiveConfigKey[workloadIssue];
    if (!aggressiveConfigKey) {
      return { type: 'incompatible' }; // workload issue with no ignore option
    }
    config[aggressiveConfigKey] = true;
  }

  const nodeIssues = node.issues?.node ?? [];

  for (const nodeIssue of nodeIssues) {
    if (nodeIssue.category !== 'REMOVAL_DISABLED') {
      return { type: 'incompatible' }; // only removal-disabled node issues are ignorable
    }
    config.ignoreProblemRemovalDisabledPods = true;
  }

  return { type: 'required', config };
};

export const isRequiredAggressiveModeConfigCovered = ({
  required,
  provided,
}: {
  required: AggressiveModeConfig;
  provided: AggressiveModeConfig;
}): boolean => {
  const requiredKeys = Object.keys(required) as (keyof AggressiveModeConfig)[];
  return requiredKeys.every((key) => !required[key] || provided[key]);
};

const applyNodeRequirements = ({
  aggregatedAggressiveModeConfig,
  nodeAggressiveModeConfig,
}: {
  aggregatedAggressiveModeConfig: AggressiveModeConfig;
  nodeAggressiveModeConfig: AggressiveModeConfig;
}): void => {
  for (const key of Object.keys(
    nodeAggressiveModeConfig
  ) as (keyof AggressiveModeConfig)[]) {
    if (nodeAggressiveModeConfig[key]) {
      aggregatedAggressiveModeConfig[key] = true;
    }
  }
};

type PartitionNodesByEligibilityResult = {
  /** Rebalanceable under the current aggressive mode  */
  eligible: RebalancingConfigurationNode[];
  /** Aggressive nodes not yet covered by the current config. Would become eligible if it changed. */
  requiresAggressiveModeChange: RebalancingConfigurationNode[];
  /** Never rebalanceable, regardless of config */
  notRebalanceable: RebalancingConfigurationNode[];
};

export const partitionNodesByEligibility = ({
  nodes,
  currentAggressiveMode = false,
  currentAggressiveModeConfig = AGGRESSIVE_MODE_CONFIG_WITH_ALL_FLAGS_DISABLED,
}: {
  nodes: RebalancingConfigurationNode[];
  currentAggressiveMode?: boolean;
  currentAggressiveModeConfig?: AggressiveModeConfig;
}): PartitionNodesByEligibilityResult => {
  const eligible: RebalancingConfigurationNode[] = [];
  const requiresAggressiveModeChange: RebalancingConfigurationNode[] = [];
  const notRebalanceable: RebalancingConfigurationNode[] = [];

  nodes.forEach((node) => {
    const requiredAggressiveModeConfigForNodeResult =
      getNodeAggressiveModeRequirement(node);
    switch (requiredAggressiveModeConfigForNodeResult.type) {
      case 'not_needed':
        eligible.push(node);
        break;
      case 'incompatible':
        notRebalanceable.push(node);
        break;
      case 'required': {
        const isConfigCoversRequired =
          currentAggressiveMode &&
          isRequiredAggressiveModeConfigCovered({
            required: requiredAggressiveModeConfigForNodeResult.config,
            provided: currentAggressiveModeConfig,
          });
        if (isConfigCoversRequired) {
          eligible.push(node);
        } else {
          requiresAggressiveModeChange.push(node);
        }
        break;
      }
      default:
        return notReachable(requiredAggressiveModeConfigForNodeResult);
    }
  });

  return {
    eligible,
    requiresAggressiveModeChange,
    notRebalanceable,
  };
};

type GetRequiredAggressiveModeConfigResult = {
  requiredAggressiveModeConfig: AggressiveModeConfig;
  isAggressiveModeRequired: boolean;
};

export const getRequiredAggressiveModeConfig = (
  nodes: RebalancingConfigurationNode[]
): GetRequiredAggressiveModeConfigResult => {
  const requiredAggressiveModeConfig: AggressiveModeConfig = {
    ...AGGRESSIVE_MODE_CONFIG_WITH_ALL_FLAGS_DISABLED,
  };

  nodes.forEach((node) => {
    const requiredAggressiveModeConfigForNodeResult =
      getNodeAggressiveModeRequirement(node);
    switch (requiredAggressiveModeConfigForNodeResult.type) {
      case 'not_needed':
      case 'incompatible':
        break;
      case 'required': {
        const isConfigCoversRequired = isRequiredAggressiveModeConfigCovered({
          required: requiredAggressiveModeConfigForNodeResult.config,
          provided: requiredAggressiveModeConfig,
        });
        if (!isConfigCoversRequired) {
          applyNodeRequirements({
            aggregatedAggressiveModeConfig: requiredAggressiveModeConfig,
            nodeAggressiveModeConfig:
              requiredAggressiveModeConfigForNodeResult.config,
          });
        }
        break;
      }
      default:
        return notReachable(requiredAggressiveModeConfigForNodeResult);
    }
  });

  const isAggressiveModeRequired = Object.values(
    requiredAggressiveModeConfig
  ).some((enabled) => enabled);

  return {
    requiredAggressiveModeConfig,
    isAggressiveModeRequired,
  };
};

type RebalancingEligibilityResult = PartitionNodesByEligibilityResult &
  GetRequiredAggressiveModeConfigResult;

export const evaluateNodesForRebalancing = ({
  nodes,
  currentAggressiveMode,
  currentAggressiveModeConfig,
}: {
  nodes: RebalancingConfigurationNode[];
  currentAggressiveMode: boolean;
  currentAggressiveModeConfig: AggressiveModeConfig;
}): RebalancingEligibilityResult => {
  const { eligible, requiresAggressiveModeChange, notRebalanceable } =
    partitionNodesByEligibility({
      nodes,
      currentAggressiveMode,
      currentAggressiveModeConfig,
    });

  // Passing all nodes, not just nodes that are not covered by the current config
  // so the result includes flags already active for eligible nodes
  const { requiredAggressiveModeConfig, isAggressiveModeRequired } =
    getRequiredAggressiveModeConfig(nodes);

  return {
    eligible,
    requiresAggressiveModeChange,
    notRebalanceable,
    requiredAggressiveModeConfig,
    isAggressiveModeRequired,
  };
};

export const getResourceComponent = ({
  node,
  type,
}: {
  node: RebalancingConfigurationNode;
  type: RebalancingConfigurationNodeComponentType;
}): RebalancingConfigurationNodeComponent | undefined => {
  return node.components?.find((component) => component.type === type);
};

export const getResourceUtilizationPercent = ({
  capacity,
  requested,
}: {
  capacity: number;
  requested: number;
}): number => {
  if (capacity === 0) {
    return 0;
  }
  return (requested * 100) / capacity;
};

export const getGpuAmount = (
  node: RebalancingConfigurationNode
): number | undefined => {
  const component = getResourceComponent({
    node,
    type: 'GPU',
  });
  return component?.amount;
};

export const getMemoryCapacityInMib = (
  node: RebalancingConfigurationNode
): number | undefined => {
  const component = getResourceComponent({
    node,
    type: 'MEMORY',
  });

  const amount = toNumOrZero(component?.amount);
  switch (component?.unit) {
    case 'GIB':
      return amount * 1024;
    case 'MIB':
      return amount;
    default:
      return undefined;
  }
};

export const getCpuCapacityInMilli = (
  node: RebalancingConfigurationNode
): number | undefined => {
  const component = getResourceComponent({
    node,
    type: 'CPU',
  });

  const amount = toNumOrZero(component?.amount);
  switch (component?.unit) {
    case 'COUNT':
      return amount * 1000;
    case 'MILLI_COUNT':
      return amount;
    default:
      return undefined;
  }
};

export const getNodeCpuPricing = (
  node: RebalancingConfigurationNode
): number | undefined => {
  const component = getResourceComponent({
    node,
    type: 'CPU',
  });
  const pricing = component?.price;

  if (!pricing) {
    return undefined;
  }

  const totalPricePerUnit = toNumOrZero(pricing.totalPricePerUnit);
  const cpuCapacityMili = getCpuCapacityInMilli(node);

  if (!cpuCapacityMili) {
    return undefined;
  }

  switch (pricing.unit) {
    case 'COUNT':
      return totalPricePerUnit * (cpuCapacityMili / 1000);
    case 'MILLI_COUNT':
      return totalPricePerUnit * cpuCapacityMili;
    default:
      return undefined;
  }
};

export const getNodeMemoryPricing = (
  node: RebalancingConfigurationNode
): number | undefined => {
  const component = getResourceComponent({
    node,
    type: 'MEMORY',
  });
  const pricing = component?.price;

  if (!pricing) {
    return undefined;
  }
  const totalPricePerUnit = toNumOrZero(pricing.totalPricePerUnit);

  const memoryCapacityMib = getMemoryCapacityInMib(node);

  if (!memoryCapacityMib) {
    return undefined;
  }

  switch (pricing.unit) {
    case 'GIB':
      return totalPricePerUnit * convertUnit(memoryCapacityMib, 'MiB', 'GiB');
    case 'MIB':
      return totalPricePerUnit * memoryCapacityMib;
    default:
      return undefined;
  }
};

export const getNodeGpuPricing = (
  node: RebalancingConfigurationNode
): number | undefined => {
  const component = getResourceComponent({
    node,
    type: 'GPU',
  });
  const pricing = component?.price;
  const gpuAmount = getGpuAmount(node);

  if (!pricing || !gpuAmount) {
    return undefined;
  }

  const totalPricePerUnit = toNumOrZero(pricing.totalPricePerUnit);

  switch (pricing.unit) {
    case 'COUNT':
      return totalPricePerUnit * gpuAmount;
    default:
      return undefined;
  }
};

export const getCastNodeId = (
  node: RebalancingConfigurationNode
): string | undefined => {
  return node.id?.castNodeId;
};

export const getNodeName = (node: RebalancingConfigurationNode): string => {
  const name = node.id?.name;
  if (name) {
    return name;
  }

  // should never happen as name should always be present
  return '';
};

export const getTotalNodePricing = (
  node: RebalancingConfigurationNode
): number | undefined => {
  return toNumOrUndefined(node.pricing?.totalPrice);
};
